import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import {
  DeleteCustomRoleDocument,
  PermissionMatrixDocument,
  RoleInviteGrantMatrixDocument,
  UpdateCustomRoleDocument,
} from "#app/lib/data";

// Rename / delete actions for custom roles, used by the Custom Role
// Permissions matrix's per-column menu. Both refetch the permission +
// invite matrices so the column set updates in place. System roles are
// rejected server-side; the UI only shows the menu on custom rows.

export function useCustomRoleActions(workspaceId: string) {
  const refetchQueries = [
    { query: PermissionMatrixDocument, variables: { workspaceId } },
    { query: RoleInviteGrantMatrixDocument, variables: { workspaceId } },
  ];
  const [updateCustomRole] = useMutation(UpdateCustomRoleDocument, {
    refetchQueries,
  });
  const [deleteCustomRole] = useMutation(DeleteCustomRoleDocument, {
    refetchQueries,
  });

  const renameRole = useCallback(
    async (roleId: string, roleName: string) => {
      await updateCustomRole({
        variables: { input: { roleId, roleName: roleName.trim() } },
      });
    },
    [updateCustomRole],
  );

  const deleteRole = useCallback(
    // `reassignToRoleId` (optional): move every user currently holding the
    // deleted role onto this role instead of dropping the assignment. The
    // backend (permissions-admin.service) does the row migration; omitting
    // it leaves holders with one fewer role.
    async (roleId: string, reassignToRoleId?: string) => {
      await deleteCustomRole({
        variables: { input: { roleId, reassignToRoleId } },
      });
    },
    [deleteCustomRole],
  );

  return { renameRole, deleteRole };
}
